<template>
	<div>
		<JuniperPageHeader back="/juniper" title="Accessibility Stats" subtitle="Clients with scheduled accessibility audits — latest report per client" />

		<!-- Totals across all enabled clients — own card above the filters -->
		<div class="axs-section axs-summary-section mb-3">
			<div class="axs-summary">
				<div class="axs-summary-item">
					<div class="axs-summary-label">Clients enabled</div>
					<div class="axs-summary-value">{{ rows.length }}</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Pages in latest reports</div>
					<div class="axs-summary-value">{{ totals.pages }}</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Page status</div>
					<div class="axs-summary-value axs-summary-value--sm">
						<span class="nowrap me-3"><span class="status-dot good"></span>{{ totals.good }}</span>
						<span class="nowrap me-3"><span class="status-dot warn"></span>{{ totals.warn }}</span>
						<span class="nowrap"><span class="status-dot poor"></span>{{ totals.poor }}</span>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Errors by type</div>
					<div class="axs-summary-value axs-summary-value--sm">
						<span class="source-badge content" title="Content errors">C {{ totals.content }}</span>
						<span class="source-badge theme" title="Theme errors">T {{ totals.theme }}</span>
						<span class="source-badge document" title="Document errors">D {{ totals.document }}</span>
						<span v-if="totals.unknown" class="source-badge unknown" title="Findings from scans recorded before the content/theme resolver">? {{ totals.unknown }}</span>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Average score</div>
					<div class="axs-summary-value">{{ totals.avgScore !== null ? totals.avgScore + '%' : '—' }}</div>
				</div>
			</div>
		</div>

		<div class="axs-section mb-4">
			<div class="filter-tabs-container">
				<div class="filter-tabs-group">
					<div class="filter-tabs">
						<button class="filter-tab" :class="{ active: tab === 'clients' }" @click="tab = 'clients'">
							<span class="filter-tab-text">Clients</span>
							<span class="filter-tab-count">{{ rows.length }}</span>
						</button>
						<button class="filter-tab" :class="{ active: tab === 'info' }" @click="tab = 'info'">
							<span class="filter-tab-text">Info</span>
						</button>
					</div>

					<template v-if="tab === 'clients'">
						<div class="tabs-divider"></div>
						<div class="filter-tabs">
							<button
								v-for="f in statusFilters"
								:key="f.key"
								class="filter-tab"
								:class="{ active: statusFilter === f.key }"
								@click="statusFilter = f.key"
							>
								<span class="filter-tab-text">{{ f.label }}</span>
							</button>
						</div>
					</template>
				</div>

				<div v-if="tab === 'clients'" class="toolbar-right">
					<div class="search-input-wrapper">
						<font-awesome-icon :icon="['fas', 'search']" class="search-icon" />
						<input type="text" class="search-input" placeholder="Search client or website" v-model.trim="search" />
						<button v-if="search" type="button" class="search-clear-btn" aria-label="Clear search" @click="search = ''">
							<font-awesome-icon :icon="['fas', 'times']" />
						</button>
					</div>
					<button class="toolbar-icon-btn" title="Reload" :disabled="loading" @click="load">
						<font-awesome-icon :icon="['fas', 'sync']" :spin="loading" />
					</button>
				</div>
			</div>

			<!-- Clients tab -->
			<template v-if="tab === 'clients'">
				<div class="axs-table">
					<div class="table-header">
						<button
							v-for="col in columns"
							:key="col.key"
							type="button"
							class="header-cell"
							:class="{ 'text-end': col.end, sortable: col.sort }"
							:disabled="!col.sort"
							@click="col.sort && setSort(col.key)"
						>
							{{ col.label }}
							<font-awesome-icon v-if="col.sort && sort.key === col.key" :icon="['fas', sort.dir === 'asc' ? 'chevron-up' : 'chevron-down']" class="ms-1" />
						</button>
					</div>

					<div class="table-body-wrap" :class="{ 'is-loading': loading }">
						<div class="table-body" :class="{ 'is-blurred': loading }">
							<div v-if="!loading && visibleRows.length === 0" class="empty-state">
								<div class="empty-state-icon">
									<font-awesome-icon :icon="['fas', 'universal-access']" />
								</div>
								<h3 class="empty-state-title">{{ error ? 'Could not load stats' : 'No clients' }}</h3>
								<p class="empty-state-description">
									{{ error || (search || statusFilter !== 'all' ? 'Try adjusting the filters' : 'No client has scheduled accessibility audits (Client → Settings → Audits)') }}
								</p>
							</div>

							<div
								v-for="row in pagedRows"
								:key="row.client_id"
								class="table-row"
								:class="{ 'is-clickable': row.last_report }"
								:title="row.last_report ? 'Reports & changes for this client' : ''"
								@click="openClient(row)"
							>
								<div class="table-cell client-cell">
									<span class="client-name-text">{{ row.name || 'Client #' + row.client_id }}</span>
									<span class="client-url">{{ shortUrl(row.url) }} · {{ engines(row.engine) }}</span>
								</div>
								<div class="table-cell" :title="row.project_manager || ''">
									<span v-if="row.project_manager" class="engine-text">{{ row.project_manager }}</span>
									<span v-else class="cell-muted">—</span>
								</div>
								<div class="table-cell" :title="row.theme">
									<span class="theme-badge">{{ row.theme }}</span>
								</div>
								<div class="table-cell schedule-cell" :title="scheduleTitle(row.schedule)">
									<span class="interval-badge">{{ row.interval }}</span>
									<span class="engine-text">{{ scheduleWhen(row.schedule) }}</span>
								</div>
								<div class="table-cell" :title="nextScan(row.schedule, row.last_report).title">
									<span class="next-scan" :class="nextScan(row.schedule, row.last_report).state">{{ nextScan(row.schedule, row.last_report).text }}</span>
								</div>
								<div class="table-cell">
									<template v-if="row.last_report">
										<span class="time-text" :title="formatUtc(lastAt(row))">{{ fromNowUtc(lastAt(row)) }}</span>
										<span v-if="row.last_report.status !== 'finished'" class="level-badge info ms-1">{{ row.last_report.status }}</span>
									</template>
									<span v-else class="cell-muted">Never</span>
								</div>
								<div class="table-cell text-end">
									<template v-if="row.last_report">
										{{ row.last_report.pages }}
										<span v-if="row.last_report.status !== 'finished' && row.last_report.pages_expected" class="cell-muted">/ {{ row.last_report.pages_expected }}</span>
									</template>
									<span v-else class="cell-muted">—</span>
								</div>
								<div class="table-cell">
									<div v-if="row.last_report && row.last_report.pages" class="status-cell">
										<div class="status-bar" :title="statusTitle(row.last_report)">
											<span class="seg good" :style="{ width: pct(row.last_report.good_pages, row.last_report.pages) }"></span>
											<span class="seg warn" :style="{ width: pct(row.last_report.warn_pages, row.last_report.pages) }"></span>
											<span class="seg poor" :style="{ width: pct(row.last_report.poor_pages, row.last_report.pages) }"></span>
										</div>
										<span class="status-nums">
											<span class="good-text">{{ row.last_report.good_pages }}</span> ·
											<span class="warn-text">{{ row.last_report.warn_pages }}</span> ·
											<span class="poor-text">{{ row.last_report.poor_pages }}</span>
										</span>
									</div>
									<span v-else class="cell-muted">—</span>
								</div>
								<div class="table-cell text-end">{{ row.last_report ? row.last_report.errors : '—' }}</div>
								<div class="table-cell text-end">{{ row.last_report ? row.last_report.warnings : '—' }}</div>
								<div class="table-cell">
									<template v-if="row.last_report">
										<span class="source-badge content" title="Content errors">C {{ row.last_report.errors_by_source.content }}</span>
										<span class="source-badge theme" title="Theme errors">T {{ row.last_report.errors_by_source.theme }}</span>
										<span class="source-badge document" title="Document errors">D {{ row.last_report.errors_by_source.document }}</span>
									</template>
									<span v-else class="cell-muted">—</span>
								</div>
								<div class="table-cell text-end">
									<span v-if="row.last_report && row.last_report.score !== null" class="score-badge" :class="scoreClass(row.last_report.score)">
										{{ row.last_report.score }}%
									</span>
									<span v-else class="cell-muted">—</span>
								</div>
								<div class="table-cell text-end">{{ row.reports }}</div>
								<div class="table-cell text-end">
									<router-link
										class="row-link-btn"
										:to="`/juniper/clients/edit/${row.client_id}`"
										title="Open client"
										@click.native.stop
									>
										<font-awesome-icon :icon="['fas', 'link']" />
									</router-link>
								</div>
							</div>
						</div>

						<div v-if="loading" class="table-loading">
							<div class="table-loading__box">
								<span class="spinner-border spinner-border-sm me-2" role="status"></span>
								Loading accessibility stats…
							</div>
						</div>
					</div>

					<StatsPagination :total="visibleRows.length" :page.sync="page" :per-page.sync="perPage" />
				</div>
			</template>

			<!-- Info tab -->
			<div v-else class="axs-info">
				<section class="info-block">
					<h3 class="info-title">Where a problem lives</h3>
					<p class="info-text">
						Every finding is traced back to the HTML that produced it (mc2 PbElementResolver). That decides who fixes it:
					</p>
					<div class="axs-info-grid">
						<div class="info-card">
							<span class="source-badge content">Content</span>
							<p>
								HTML an editor wrote in HeyGov — pages, news, departments, FAQs, business listings (juniper marks it with
								<code>jr:content</code>). The client fixes it in the editor; the report pencil links straight to it.
								Typical: images without alt text, embedded iframes, pasted forms.
							</p>
						</div>
						<div class="info-card">
							<span class="source-badge theme">Theme</span>
							<p>
								Everything the theme template and page-builder elements render — header, menus, footer, sliders, calendars,
								meetings, colors from the customizer. We fix it once in the theme / element code and it is fixed on every
								site using it.
							</p>
						</div>
						<div class="info-card">
							<span class="source-badge document">Document</span>
							<p>
								Linked files, mostly PDFs (TWAC pdf checks): untagged or scanned PDFs, missing titles/language. Fixed by
								re-exporting an accessible file and replacing it in the media library.
							</p>
						</div>
						<div class="info-card">
							<span class="source-badge unknown">Unclassified</span>
							<p>
								Findings from scans recorded before source tracking, or nodes the resolver could not find again in the
								published page. They drop out as clients are re-scanned.
							</p>
						</div>
					</div>
				</section>

				<section class="info-block">
					<h3 class="info-title">Page status &amp; score</h3>
					<ul class="info-list">
						<li><span class="status-dot good"></span><strong>Great</strong> — no errors and no warnings.</li>
						<li><span class="status-dot warn"></span><strong>Needs improvement</strong> — no errors, some warnings (review items).</li>
						<li><span class="status-dot poor"></span><strong>Poor</strong> — at least one error.</li>
						<li><strong>Page score</strong> = 100 − 5 × errors (floored at 0); warnings never lower it.</li>
						<li><strong>Report score</strong> = share of pages with zero errors (Great + Needs improvement).</li>
					</ul>
				</section>

				<section class="info-block">
					<h3 class="info-title">Engines</h3>
					<ul class="info-list">
						<li><strong>AXE</strong> (axe-core) — industry standard, almost no false positives; what Lighthouse and most external checkers use.</li>
						<li><strong>IBM</strong> (Equal Access) — stricter, more rules (e.g. keyboard handling of widgets), also "needs review" items.</li>
						<li><strong>TWAC</strong> — our own checks: broken links, PDF accessibility. Always on.</li>
						<li>
							When AXE and IBM report the same problem on the same element they are merged into one finding (one card, one
							error) — the rules below. Rules that only share an element but describe different problems stay separate.
						</li>
					</ul>
				</section>

				<section class="info-block">
					<h3 class="info-title">Merged rules</h3>
					<div class="axs-table rules-table">
						<div class="table-header">
							<div class="header-cell">Name in report</div>
							<div class="header-cell">AXE rule</div>
							<div class="header-cell">IBM rule</div>
							<div class="header-cell">Likely type</div>
							<div class="header-cell">Why</div>
						</div>
						<div class="table-body-wrap" :class="{ 'is-loading': rulesLoading }">
							<div v-if="!rulesLoading && rules.length === 0" class="empty-state">
								<p class="empty-state-description">Could not load the rule map.</p>
							</div>
							<div v-for="r in rules" :key="r.key" class="table-row">
								<div class="table-cell wrap client-name-text">{{ r.title }}</div>
								<div class="table-cell wrap"><code v-for="id in r.axe" :key="id" class="rule-code">{{ id }}</code></div>
								<div class="table-cell wrap"><code v-for="id in r.ibm" :key="id" class="rule-code">{{ id }}</code></div>
								<div class="table-cell">
									<span class="source-badge" :class="r.source">{{ r.source }}</span>
								</div>
								<div class="table-cell wrap why-cell">{{ r.why }}</div>
							</div>
							<div v-if="rulesLoading" class="table-loading">
								<div class="table-loading__box">
									<span class="spinner-border spinner-border-sm me-2" role="status"></span>
									Loading rules…
								</div>
							</div>
						</div>
					</div>
					<p class="info-note">
						"Likely type" is where the problem usually lives; each finding's real type is decided per element when the page is
						scanned.
					</p>
				</section>
			</div>
		</div>

	</div>
</template>

<script>
import api from '@/api'
import { fromNowUtc, formatUtc } from '@/utils/time'
import StatsPagination from '@/components/juniper/ada-auditor/StatsPagination.vue'
import { scheduleWhen, scheduleTitle, nextScan } from '@/components/juniper/ada-auditor/auditSchedule'

export default {
	name: 'AccessibilityStats',
	components: { StatsPagination },
	data() {
		return {
			tab: 'clients',
			rows: [],
			loading: false,
			error: null,
			rules: [],
			rulesLoading: false,
			search: '',
			statusFilter: 'all',
			statusFilters: [
				{ key: 'all', label: 'All' },
				{ key: 'poor', label: 'With errors' },
				{ key: 'clean', label: 'No errors' },
				{ key: 'never', label: 'Never audited' },
			],
			sort: { key: 'name', dir: 'asc' },
			page: 1,
			perPage: 10,
			columns: [
				{ key: 'name', label: 'Client', sort: true },
				{ key: 'project_manager', label: 'PM', sort: true },
				{ key: 'theme', label: 'Theme', sort: true },
				{ key: 'interval', label: 'Schedule', sort: true },
				{ key: 'next_at', label: 'Next scan', sort: true },
				{ key: 'last_at', label: 'Last audit', sort: true },
				{ key: 'pages', label: 'Pages', sort: true, end: true },
				{ key: 'status', label: 'Page status' },
				{ key: 'errors', label: 'Errors', sort: true, end: true },
				{ key: 'warnings', label: 'Warnings', sort: true, end: true },
				{ key: 'source', label: 'Errors by type' },
				{ key: 'score', label: 'Score', sort: true, end: true },
				{ key: 'reports', label: 'Reports', sort: true, end: true },
				{ key: 'actions', label: '' },
			],
		}
	},
	computed: {
		visibleRows() {
			const q = this.search.toLowerCase()
			const rows = this.rows.filter((r) => {
				if (q && !`${r.name || ''} ${r.url || ''}`.toLowerCase().includes(q)) return false
				const last = r.last_report
				if (this.statusFilter === 'never') return !last
				if (this.statusFilter === 'poor') return !!last && last.errors > 0
				if (this.statusFilter === 'clean') return !!last && last.errors === 0
				return true
			})
			const dir = this.sort.dir === 'asc' ? 1 : -1
			return rows.sort((a, b) => {
				const va = this.sortValue(a)
				const vb = this.sortValue(b)
				// rows without a value (never audited) always sink to the bottom
				if (va === null && vb === null) return 0
				if (va === null) return 1
				if (vb === null) return -1
				return va < vb ? -dir : va > vb ? dir : 0
			})
		},
		pagedRows() {
			const from = (this.page - 1) * this.perPage
			return this.visibleRows.slice(from, from + this.perPage)
		},
		totals() {
			const t = { pages: 0, good: 0, warn: 0, poor: 0, content: 0, theme: 0, document: 0, unknown: 0, avgScore: null }
			const scores = []
			for (const r of this.rows) {
				const last = r.last_report
				if (!last) continue
				t.pages += last.pages
				t.good += last.good_pages
				t.warn += last.warn_pages
				t.poor += last.poor_pages
				for (const key of ['content', 'theme', 'document', 'unknown']) t[key] += last.errors_by_source[key] || 0
				if (last.score !== null) scores.push(last.score)
			}
			if (scores.length) t.avgScore = Math.round(scores.reduce((s, v) => s + v, 0) / scores.length)
			return t
		},
	},
	watch: {
		// any filter/sort change starts over on page 1
		search() {
			this.page = 1
		},
		statusFilter() {
			this.page = 1
		},
		sort: {
			deep: true,
			handler() {
				this.page = 1
			},
		},
	},
	created() {
		this.load()
		this.loadRules()
	},
	methods: {
		fromNowUtc,
		formatUtc,
		scheduleWhen,
		scheduleTitle,
		nextScan,
		async load() {
			this.loading = true
			this.error = null
			try {
				const { data } = await api.get('/juniper/ada-auditor/stats/clients')
				this.rows = data.data || []
			} catch (e) {
				this.rows = []
				this.error = (e.response && e.response.data && e.response.data.message) || 'Request failed'
			} finally {
				this.loading = false
			}
		},
		async loadRules() {
			this.rulesLoading = true
			try {
				const { data } = await api.get('/juniper/ada-auditor/stats/rules')
				this.rules = data.data || []
			} catch (e) {
				this.rules = []
			} finally {
				this.rulesLoading = false
			}
		},
		setSort(key) {
			if (this.sort.key === key) {
				this.sort.dir = this.sort.dir === 'asc' ? 'desc' : 'asc'
			} else {
				// names read A→Z; numbers and dates are most useful biggest/newest first
				this.sort = { key, dir: ['name', 'project_manager', 'theme', 'interval', 'next_at'].includes(key) ? 'asc' : 'desc' }
			}
		},
		sortValue(r) {
			const last = r.last_report
			switch (this.sort.key) {
				case 'name':
					return (r.name || '').toLowerCase()
				case 'interval':
					return r.interval || ''
				case 'theme':
					return r.theme || ''
				case 'project_manager':
					// no PM sinks to the bottom like other empty values
					return r.project_manager ? r.project_manager.toLowerCase() : null
				case 'last_at':
					return last ? this.lastAt(r) || null : null
				case 'next_at':
					return r.schedule ? r.schedule.next_run_at : null
				case 'reports':
					return r.reports
				case 'pages':
				case 'errors':
				case 'warnings':
				case 'score':
					return last && last[this.sort.key] !== null ? last[this.sort.key] : null
				default:
					return null
			}
		},
		lastAt(r) {
			return r.last_report ? r.last_report.finished_at || r.last_report.started_at : null
		},
		openClient(row) {
			if (!row.last_report) return
			this.$router.push({ name: 'juniperAccessibilityClientStats', params: { clientId: row.client_id } })
		},
		engines(engine) {
			return (engine || 'IBM').split('+').filter(Boolean).join(' · ')
		},
		shortUrl(url) {
			return (url || '').replace(/^https?:\/\//, '').replace(/\/$/, '')
		},
		pct(part, total) {
			return total ? (100 * part) / total + '%' : '0%'
		},
		statusTitle(last) {
			return `Great ${last.good_pages} · Needs improvement ${last.warn_pages} · Poor ${last.poor_pages}`
		},
		scoreClass(score) {
			if (score >= 90) return 'good'
			if (score >= 70) return 'warn'
			return 'poor'
		},
	},
}
</script>

<style scoped src="./accessibility-stats.css"></style>
